import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { supabase } from '@/lib/supabase'
import { format, formatDistanceToNow } from 'date-fns'
import { createDogNameMap } from '@/lib/dogNames'

interface Visitor {
  id: string
  site_id: string
  visitor_hash: string
  first_seen_at: string
  last_seen_at: string
  total_sessions: number
  total_pageviews: number
  total_events: number
  is_converted: boolean
  first_converted_at: string | null
  first_source_type: string | null
  first_source_name: string | null
  first_source_medium: string | null
  first_referrer: string | null
  first_utm_source: string | null
  first_utm_campaign: string | null
  last_source_type: string | null
  last_source_name: string | null
}

type SortKey = 'last_seen_at' | 'first_seen_at' | 'total_sessions' | 'total_pageviews'
type SortDirection = 'asc' | 'desc'
type FilterType = 'all' | 'converted' | 'returning'

const sourceTypeColors: Record<string, string> = {
  'organic': 'bg-green-100 text-green-700',
  'paid': 'bg-blue-100 text-blue-700',
  'ai': 'bg-purple-100 text-purple-700',
  'social': 'bg-pink-100 text-pink-700',
  'email': 'bg-yellow-100 text-yellow-700',
  'referral': 'bg-orange-100 text-orange-700',
  'direct': 'bg-gray-100 text-gray-700',
}

const sourceTypeIcons: Record<string, string> = {
  'organic': '🔍',
  'paid': '💰',
  'ai': '🤖',
  'social': '📱',
  'email': '📧',
  'referral': '🔗',
  'direct': '🔒',
}

export default function VisitorsPage() {
  const { site } = useAuthStore()
  const [visitors, setVisitors] = useState<Visitor[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: SortDirection }>({
    key: 'last_seen_at',
    direction: 'desc',
  })
  const [filter, setFilter] = useState<FilterType>('all')
  const [sourceTypeFilter, setSourceTypeFilter] = useState<string>('all')
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const limit = 50

  useEffect(() => {
    if (site) {
      fetchVisitors()
    }
  }, [site, page, sortConfig, filter, sourceTypeFilter])

  const fetchVisitors = async () => {
    if (!site) return
    setIsLoading(true)

    try {
      // Build base query for count
      let countQuery = supabase
        .from('visitors')
        .select('*', { count: 'exact', head: true })
        .eq('site_id', site.id)

      // Build data query
      let dataQuery = supabase
        .from('visitors')
        .select('*')
        .eq('site_id', site.id)

      // Apply filters
      if (filter === 'converted') {
        countQuery = countQuery.eq('is_converted', true)
        dataQuery = dataQuery.eq('is_converted', true)
      } else if (filter === 'returning') {
        countQuery = countQuery.gt('total_sessions', 1)
        dataQuery = dataQuery.gt('total_sessions', 1)
      }

      if (sourceTypeFilter !== 'all') {
        countQuery = countQuery.eq('first_source_type', sourceTypeFilter)
        dataQuery = dataQuery.eq('first_source_type', sourceTypeFilter)
      }

      // Get count
      const { count } = await countQuery
      setTotalCount(count || 0)
      setTotalPages(Math.ceil((count || 0) / limit))

      // Apply sorting and pagination
      dataQuery = dataQuery.order(sortConfig.key, { ascending: sortConfig.direction === 'asc' })
      dataQuery = dataQuery.range((page - 1) * limit, page * limit - 1)

      const { data } = await dataQuery

      setVisitors((data || []) as Visitor[])
    } catch (error) {
      console.error('Failed to fetch visitors:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Create dog name map for current visitors
  const dogNameMap = useMemo(() => {
    const hashes = visitors.map(v => v.visitor_hash)
    return createDogNameMap(hashes)
  }, [visitors])

  const handleSort = (key: SortKey) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }))
    setPage(1)
  }

  const toggleRowExpand = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const exportCSV = () => {
    if (visitors.length === 0) return

    const headers = [
      'Visitor Name', 'First Seen', 'Last Seen', 'Total Sessions', 'Total Pageviews',
      'Converted', 'First Source Type', 'First Source', 'First Campaign'
    ]
    const rows = visitors.map(v => [
      dogNameMap.get(v.visitor_hash) || v.visitor_hash.slice(0, 8),
      v.first_seen_at,
      v.last_seen_at,
      v.total_sessions,
      v.total_pageviews,
      v.is_converted ? 'Yes' : 'No',
      v.first_source_type || '',
      v.first_source_name || '',
      v.first_utm_campaign || '',
    ])

    const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `visitors-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortConfig.key !== column) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      )
    }
    return sortConfig.direction === 'desc' ? (
      <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
    )
  }

  if (!site) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No site configured</h2>
        <p className="text-gray-600 mb-4">Add your website to start tracking visitors.</p>
        <Link to="/settings" className="btn-primary">Add your site</Link>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Visitors</h1>
          <p className="text-gray-600">Track unique visitors across sessions</p>
        </div>
        <button onClick={exportCSV} disabled={visitors.length === 0} className="btn-secondary">
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Main filter */}
          <div>
            <label className="label mb-1 block">Filter</label>
            <div className="flex gap-2">
              {(['all', 'converted', 'returning'] as FilterType[]).map((f) => (
                <button
                  key={f}
                  onClick={() => { setFilter(f); setPage(1) }}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors capitalize ${
                    filter === f
                      ? 'bg-primary-100 border-primary-300 text-primary-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {f === 'all' ? 'All Visitors' : f === 'converted' ? 'Converted' : 'Returning'}
                </button>
              ))}
            </div>
          </div>

          {/* Source type filter */}
          <div>
            <label className="label mb-1 block">Traffic Source</label>
            <select
              value={sourceTypeFilter}
              onChange={(e) => { setSourceTypeFilter(e.target.value); setPage(1) }}
              className="input"
            >
              <option value="all">All Sources</option>
              <option value="organic">Organic Search</option>
              <option value="paid">Paid Ads</option>
              <option value="ai">AI Tools</option>
              <option value="social">Social Media</option>
              <option value="email">Email</option>
              <option value="referral">Referral</option>
              <option value="direct">Direct</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <p className="text-sm text-gray-500">Total Visitors</p>
          <p className="text-2xl font-bold text-gray-900">{totalCount.toLocaleString()}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-gray-500">Converted</p>
          <p className="text-2xl font-bold text-green-600">
            {visitors.filter(v => v.is_converted).length}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-gray-500">Returning</p>
          <p className="text-2xl font-bold text-blue-600">
            {visitors.filter(v => v.total_sessions > 1).length}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-gray-500">Avg Sessions</p>
          <p className="text-2xl font-bold text-gray-900">
            {visitors.length > 0
              ? (visitors.reduce((sum, v) => sum + v.total_sessions, 0) / visitors.length).toFixed(1)
              : '0'}
          </p>
        </div>
      </div>

      {/* Results count */}
      {!isLoading && (
        <div className="mb-4 text-sm text-gray-600">
          {totalCount.toLocaleString()} visitors found
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : visitors.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-600">No visitors found</p>
          <p className="text-sm text-gray-500 mt-1">
            Visitors will appear here once they're tracked
          </p>
        </div>
      ) : (
        <>
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b bg-gray-50">
                  <th className="px-4 py-3 font-medium">Visitor</th>
                  <th className="px-4 py-3 font-medium">
                    <button
                      onClick={() => handleSort('first_seen_at')}
                      className="flex items-center gap-1 hover:text-gray-700"
                    >
                      First Seen
                      <SortIcon column="first_seen_at" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">
                    <button
                      onClick={() => handleSort('last_seen_at')}
                      className="flex items-center gap-1 hover:text-gray-700"
                    >
                      Last Seen
                      <SortIcon column="last_seen_at" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">
                    <button
                      onClick={() => handleSort('total_sessions')}
                      className="flex items-center gap-1 hover:text-gray-700"
                    >
                      Sessions
                      <SortIcon column="total_sessions" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">
                    <button
                      onClick={() => handleSort('total_pageviews')}
                      className="flex items-center gap-1 hover:text-gray-700"
                    >
                      Pageviews
                      <SortIcon column="total_pageviews" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">Source</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {visitors.map((visitor) => {
                  const isExpanded = expandedRows.has(visitor.id)
                  const dogName = dogNameMap.get(visitor.visitor_hash)
                  const sourceType = visitor.first_source_type || 'direct'
                  const sourceColor = sourceTypeColors[sourceType] || sourceTypeColors.direct
                  const sourceIcon = sourceTypeIcons[sourceType] || '🔗'

                  return (
                    <tr key={visitor.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-xs font-medium text-primary-700">
                              {(dogName || visitor.visitor_hash.slice(0, 2)).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-gray-900">
                            {dogName || visitor.visitor_hash.slice(0, 8)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {format(new Date(visitor.first_seen_at), 'MMM d, yyyy')}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {formatDistanceToNow(new Date(visitor.last_seen_at), { addSuffix: true })}
                      </td>
                      <td className="px-4 py-3 text-gray-900 font-medium">
                        {visitor.total_sessions}
                        {visitor.total_sessions > 1 && (
                          <span className="ml-1 text-blue-600 text-xs">returning</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {visitor.total_pageviews}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${sourceColor}`}>
                          <span>{sourceIcon}</span>
                          <span className="capitalize">{visitor.first_source_name || sourceType}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {visitor.is_converted ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            Converted
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleRowExpand(visitor.id)}
                          className="text-primary-600 hover:text-primary-700 text-xs font-medium"
                        >
                          {isExpanded ? 'Hide' : 'Show'}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Expanded details */}
          {visitors.filter(v => expandedRows.has(v.id)).map((visitor) => (
            <div key={`detail-${visitor.id}`} className="card mt-2 p-4 bg-gray-50">
              <h4 className="font-medium text-gray-900 mb-3">
                {dogNameMap.get(visitor.visitor_hash) || visitor.visitor_hash.slice(0, 8)} - Details
              </h4>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 mb-1">First Touch Attribution</p>
                  <ul className="space-y-1">
                    <li><span className="text-gray-400">Source Type:</span> {visitor.first_source_type || 'Direct'}</li>
                    <li><span className="text-gray-400">Source:</span> {visitor.first_source_name || '-'}</li>
                    <li><span className="text-gray-400">Medium:</span> {visitor.first_source_medium || '-'}</li>
                    <li><span className="text-gray-400">Referrer:</span> {visitor.first_referrer || '-'}</li>
                    <li><span className="text-gray-400">UTM Source:</span> {visitor.first_utm_source || '-'}</li>
                    <li><span className="text-gray-400">Campaign:</span> {visitor.first_utm_campaign || '-'}</li>
                  </ul>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Last Touch Attribution</p>
                  <ul className="space-y-1">
                    <li><span className="text-gray-400">Source Type:</span> {visitor.last_source_type || 'Direct'}</li>
                    <li><span className="text-gray-400">Source:</span> {visitor.last_source_name || '-'}</li>
                  </ul>
                  {visitor.is_converted && visitor.first_converted_at && (
                    <div className="mt-4">
                      <p className="text-gray-500 mb-1">Conversion</p>
                      <p className="text-green-600 font-medium">
                        Converted on {format(new Date(visitor.first_converted_at), 'MMM d, yyyy')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-secondary px-3 py-1.5 text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="btn-secondary px-3 py-1.5 text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
